import React, { useMemo, useState } from 'react';

import { Popover } from '@mui/material';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import {
    formatMonthYearPtBr,
    monthShortNamesPtBr,
    normalizeMonthStart
} from '../utils/dateUtils';
import './monthRangePicker.css';

const getMonthTimestamp = (date) => date
    ? normalizeMonthStart(date).getTime()
    : null;

const formatShortMonthYear = (date) => {
    const month = monthShortNamesPtBr[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);

    return `${month} ${year}`;
};

const monthOptions = monthShortNamesPtBr.map((label, monthIndex) => ({ monthIndex, label }));

const MonthRangePicker = ({
    disabled = false,
    endDate,
    maxDate,
    minDate,
    onApply,
    startDate
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [draftStartDate, setDraftStartDate] = useState(startDate);
    const [draftEndDate, setDraftEndDate] = useState(endDate);
    const [previewEndDate, setPreviewEndDate] = useState(null);
    const [viewYear, setViewYear] = useState(startDate.getFullYear());
    const isOpen = Boolean(anchorEl);
    const hasCompleteDraftRange = Boolean(draftStartDate && draftEndDate);
    const normalizedMinDate = useMemo(() => normalizeMonthStart(minDate), [minDate]);
    const normalizedMaxDate = useMemo(() => normalizeMonthStart(maxDate), [maxDate]);
    const rangeLabel = `${formatShortMonthYear(startDate)} até ${formatShortMonthYear(endDate)}`;
    const minYear = normalizedMinDate.getFullYear();
    const maxYear = normalizedMaxDate.getFullYear();

    const handleOpen = (event) => {
        setDraftStartDate(startDate);
        setDraftEndDate(endDate);
        setPreviewEndDate(null);
        setViewYear(startDate.getFullYear());
        setAnchorEl(event.currentTarget);
    };

    const handleCalendarChange = (monthDate) => {
        setPreviewEndDate(null);

        if (!draftStartDate || draftEndDate) {
            setDraftStartDate(monthDate);
            setDraftEndDate(null);
            return;
        }

        if (monthDate.getTime() < draftStartDate.getTime()) {
            setDraftStartDate(monthDate);
            return;
        }

        setDraftEndDate(monthDate);
    };

    const handleApply = () => {
        if (!hasCompleteDraftRange) return;
        onApply(draftStartDate, draftEndDate);
        setAnchorEl(null);
    };

    const startTimestamp = getMonthTimestamp(draftStartDate);
    const selectedEndTimestamp = getMonthTimestamp(draftEndDate);
    const previewEndTimestamp = getMonthTimestamp(previewEndDate);
    const effectiveEndTimestamp = selectedEndTimestamp ??
        (previewEndTimestamp !== null && previewEndTimestamp >= startTimestamp ? previewEndTimestamp : null);
    const minTimestamp = getMonthTimestamp(normalizedMinDate);
    const maxTimestamp = getMonthTimestamp(normalizedMaxDate);

    return (
        <>
            <button
                type='button'
                className='month-range-picker-trigger'
                aria-label='Selecionar período'
                aria-expanded={isOpen}
                disabled={disabled}
                onClick={handleOpen}
            >
                <span>{rangeLabel}</span>
                <CalendarMonthRoundedIcon fontSize='inherit' />
            </button>

            <Popover
                open={isOpen}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'month-range-picker-popover' }}
            >
                <div className='month-range-picker'>
                    <div className='month-range-picker-heading'>
                        <strong>
                            {!draftStartDate
                                ? 'Selecione o mês inicial'
                                : !draftEndDate ? 'Selecione o mês final' : 'Período selecionado'}
                        </strong>
                        <span>
                            {draftStartDate ? formatMonthYearPtBr(draftStartDate) : 'Início'}
                            {' - '}
                            {draftEndDate ? formatMonthYearPtBr(draftEndDate) : 'Fim'}
                        </span>
                    </div>

                    <div className='month-range-picker-calendar'>
                        <div className='month-range-picker-calendar-header'>
                            <button
                                type='button'
                                aria-label='Ano anterior'
                                disabled={viewYear <= minYear}
                                onClick={() => setViewYear((year) => year - 1)}
                            >
                                <KeyboardArrowLeftRoundedIcon fontSize='inherit' />
                            </button>
                            <strong>{viewYear}</strong>
                            <button
                                type='button'
                                aria-label='Próximo ano'
                                disabled={viewYear >= maxYear}
                                onClick={() => setViewYear((year) => year + 1)}
                            >
                                <KeyboardArrowRightRoundedIcon fontSize='inherit' />
                            </button>
                        </div>

                        <div className='month-range-picker-grid'>
                            {monthOptions.map(({ monthIndex, label }) => {
                                const monthDate = new Date(viewYear, monthIndex, 1);
                                const monthTimestamp = getMonthTimestamp(monthDate);
                                const disabledMonth = monthTimestamp < minTimestamp || monthTimestamp > maxTimestamp;
                                const isRangeStart = startTimestamp !== null && monthTimestamp === startTimestamp;
                                const isRangeEnd = effectiveEndTimestamp !== null && monthTimestamp === effectiveEndTimestamp;
                                const isInRange = startTimestamp !== null &&
                                    effectiveEndTimestamp !== null &&
                                    monthTimestamp >= startTimestamp &&
                                    monthTimestamp <= effectiveEndTimestamp;
                                const isPreview = selectedEndTimestamp === null && isInRange;
                                const className = [
                                    'month-range-picker-option',
                                    isInRange ? 'is-in-range' : '',
                                    isPreview ? 'is-preview' : '',
                                    isRangeStart ? 'is-range-start' : '',
                                    isRangeEnd ? 'is-range-end' : ''
                                ].filter(Boolean).join(' ');

                                return (
                                    <button
                                        key={monthIndex}
                                        type='button'
                                        className={className}
                                        disabled={disabledMonth}
                                        onClick={() => handleCalendarChange(monthDate)}
                                        onMouseEnter={() => setPreviewEndDate(monthDate)}
                                        onMouseLeave={() => setPreviewEndDate(null)}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className='month-range-picker-actions'>
                        <button
                            type='button'
                            className='month-range-picker-apply'
                            disabled={!hasCompleteDraftRange}
                            onClick={handleApply}
                        >
                            Aplicar
                        </button>
                    </div>
                </div>
            </Popover>
        </>
    );
};

export default MonthRangePicker;
